import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useRealtimeEvent } from "../lib/realtime";
import { Send, X, ArrowLeft, LifeBuoy, MessageCircle } from "@/components/icons";

/**
 * Real-time support chat.
 * - For non-admin (doctor / secretary / pharmacy): opens a direct chat with
 *   the support admin.
 * - For admin: shows the list of users who have written to support, with
 *   unread counts; click → opens the conversation thread.
 *
 * Uses the existing /api/chat endpoints and SSE realtime events.
 */
export default function SupportPopover({ open, onClose }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [target, setTarget] = useState(null); // {id, name, photo_url, role}
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [conversations, setConversations] = useState([]);
  const bottomRef = useRef();

  // Filter out tiny placeholder photo URLs (e.g. broken 8-byte PNG seeds).
  const hasValidPhoto = (url) => !!url && url.length > 100;

  // Load target (admin user) or admin conversation list when opened.
  useEffect(() => {
    if (!open || !user) return;
    if (isAdmin) {
      api.get("/support/conversations").then((r) => setConversations(r.data || []));
    } else {
      api.get("/support/admin").then((r) => setTarget(r.data));
    }
  }, [open, user, isAdmin]);

  // Load messages for the active thread (polling fallback + SSE pushes)
  useEffect(() => {
    if (!open || !target) return;
    const fetchThread = () =>
      api.get(`/chat/${target.id}`).then((r) => setMessages(r.data || []));
    fetchThread();
    const t = setInterval(fetchThread, 15000);
    return () => clearInterval(t);
  }, [open, target?.id]);

  // Realtime: refresh thread when a new chat message arrives from / to target
  useRealtimeEvent("chat:new", (m) => {
    if (!open) return;
    if (isAdmin) {
      api.get("/support/conversations").then((r) => setConversations(r.data || []));
    }
    if (target && (m.from_user_id === target.id || m.to_user_id === target.id)) {
      api.get(`/chat/${target.id}`).then((r) => setMessages(r.data));
    }
  });
  useRealtimeEvent("chat:sent", (m) => {
    if (!open || !target) return;
    if (m.to_user_id === target.id) {
      api.get(`/chat/${target.id}`).then((r) => setMessages(r.data));
    }
  });

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !target) return;
    const { data } = await api.post("/chat", {
      to_user_id: target.id,
      content: text.trim(),
    });
    setMessages((m) => [...m, data]);
    setText("");
  };

  const openConversation = (c) =>
    setTarget({ id: c.id, name: c.name, photo_url: c.photo_url, role: c.role });

  if (!open) return null;

  // Admin view, no thread open → show conversation list
  const showList = isAdmin && !target;

  return (
    <div
      className="fixed z-[55] top-20 right-6 w-[400px] h-[580px] bg-white rounded-3xl shadow-2xl border border-white/60 overflow-hidden flex flex-col"
      data-testid="support-popover"
    >
      <div className="px-4 py-3 border-b border-black/10 flex items-center justify-between bg-gradient-to-r from-[#E8FAF3] to-white">
        <div className="flex items-center gap-2">
          {isAdmin && target && (
            <button
              onClick={() => setTarget(null)}
              className="text-black/70 hover:text-black"
              data-testid="support-back"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}
          {isAdmin ? (
            <MessageCircle className="w-5 h-5 text-[#01BC8E]" />
          ) : (
            <LifeBuoy className="w-5 h-5 text-[#01BC8E]" />
          )}
          <div className="font-bold text-[#163144] text-sm">
            {showList
              ? (isAdmin ? "Chat · Doctores" : "Soporte · Conversaciones")
              : target
              ? target.name
              : "Soporte"}
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-black/55 hover:text-black"
          data-testid="support-close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Admin: conversation list */}
      {showList && (
        <div className="flex-1 overflow-y-auto p-2">
          {conversations.length === 0 && (
            <div className="text-sm text-black/55 p-8 text-center">
              {isAdmin
                ? "Ningún doctor o secretaria te ha escrito todavía."
                : "Aún no hay conversaciones de soporte."}
            </div>
          )}
          {conversations.map((c) => (
            <button
              key={c.id}
              onClick={() => openConversation(c)}
              data-testid={`support-conv-${c.id}`}
              className="w-full text-left p-3 rounded-2xl flex items-center gap-3 hover:bg-black/[0.035] transition"
            >
              <div className="w-11 h-11 rounded-full overflow-hidden flex-shrink-0 bg-[#DBEAFE] relative">
                <div className="absolute inset-0 flex items-center justify-center font-bold text-[#1B405B]">
                  {c.name?.[0]?.toUpperCase() || "?"}
                </div>
                {hasValidPhoto(c.photo_url) && (
                  <img
                    src={c.photo_url}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover"
                    onError={(e) => (e.currentTarget.style.display = "none")}
                  />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-[#163144] truncate text-sm">
                  {c.name}
                </div>
                <div className="text-xs text-black/55 truncate">
                  {c.last_message || "—"}
                </div>
              </div>
              {c.unread > 0 && (
                <span className="ml-auto text-[10px] bg-red-500 text-white rounded-full px-2 py-0.5 font-bold">
                  {c.unread}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Chat thread */}
      {!showList && target && (
        <>
          <div className="flex-1 overflow-y-auto p-4 space-y-2.5 bg-[#FAFAFA]">
            {!isAdmin && (
              <div className="text-[11px] text-center text-black/45 py-2">
                Escríbele directamente al administrador. Te responderá lo antes posible.
              </div>
            )}
            {messages.map((m) => {
              const mine = m.from_user_id === user.id;
              return (
                <div
                  key={m.id}
                  className={`flex ${mine ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-sm ${
                      mine
                        ? "bg-[#01BC8E] text-white rounded-br-md"
                        : "bg-white text-[#163144] border border-black/10 rounded-bl-md"
                    }`}
                  >
                    <div>{m.content}</div>
                    <div
                      className={`text-[10px] mt-1 ${
                        mine ? "text-white/75" : "text-black/45"
                      }`}
                    >
                      {new Date(m.created_at).toLocaleTimeString("es-ES", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
            {messages.length === 0 && (
              <div className="text-xs text-black/45 text-center py-6">
                Sin mensajes todavía. Saluda 👋
              </div>
            )}
            <div ref={bottomRef} />
          </div>
          <form
            onSubmit={send}
            className="p-2.5 border-t border-black/10 flex gap-2 bg-white"
          >
            <input
              data-testid="support-chat-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Escribe un mensaje…"
              className="flex-1 bg-[#F5F5F5] rounded-full px-4 py-2 outline-none text-sm border border-black/5"
            />
            <button
              data-testid="support-chat-send"
              type="submit"
              className="w-10 h-10 rounded-xl bg-[#01BC8E] text-white flex items-center justify-center hover:bg-[#01BC8E]"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </>
      )}

      {/* Non-admin while admin loads */}
      {!showList && !target && (
        <div className="flex-1 flex items-center justify-center text-sm text-black/50">
          Cargando soporte…
        </div>
      )}
    </div>
  );
}
